// Ported from dev-scrips-pm-react src/billing/components/service-date-picker.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '@/billing/bridge';
import { Popover, PopoverContent, PopoverTrigger } from '@/billing/bridge';
import { cn } from '@/lib/utils';

/**
 * Service-date field for the billing / claim-review services tables.
 *
 * Flutter parity (`invoice_services_widget.dart:461-483`): a **calendar date
 * picker** (`FieldType.DatePicker`, NOT a datetime spinner) whose trigger reads
 * `h:mma, MMM dd, yyyy` (e.g. "9:38PM, May 14, 2026") and whose selectable range
 * is `firstDate = now-100y … lastDate = now`. The user picks only the DATE
 * (like Flutter's `showDatePicker`); the TIME is the backend "service added"
 * timestamp and is PRESERVED across a date change (shown in the label, never
 * zeroed).
 *
 * Value/onChange use the app's no-Z ISO contract; a cleared field emits `''`.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Parse a no-Z ISO string into a local Date (null if unset / min-value sentinel). */
function parseNoZ(iso?: string | null): Date | null {
  if (!iso || /^0001-01-01/.test(iso)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(iso);
  if (!m) return null;
  const d = new Date(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
    Number(m[4] ?? '0'), Number(m[5] ?? '0'),
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Flutter's `h:mma, MMM dd, yyyy` → "9:38PM, May 14, 2026" (PM's default 12-hour form). */
export function formatServiceDate(iso?: string | null): string {
  const d = parseNoZ(iso);
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  // PM reads a per-viewer 12h / 24h preference (`@/shared/lib/time-format`, default 12h). The PA has the same store
  // since PROD-3313 but routes it through the calendar and scheduling only so far, so this is PM's default branch:
  // Flutter's `h:mma`.
  const h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const time = `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}${ampm}`;
  return `${time}, ${MONTHS[d.getMonth()]} ${dd}, ${d.getFullYear()}`;
}

function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The time component of the existing value — it's the backend "service added"
 * timestamp, so changing the DATE preserves the original time (not midnight).
 * Falls back to `00:00:00` when the value has no time / is unset.
 */
function timePart(iso?: string | null): string {
  const m = /^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2}(?::\d{2})?)/.exec(iso ?? '');
  if (!m) return '00:00:00';
  return m[1].length === 5 ? `${m[1]}:00` : m[1];
}

interface Props {
  /** Selected date as a no-Z ISO string (or empty). */
  value?: string | null;
  /** Emits `"YYYY-MM-DDThh:mm:ss"` (picked date + preserved backend time) on
   *  pick, or `''` when cleared. */
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function ServiceDatePicker({ value, onChange, disabled, className }: Props) {
  const [open, setOpen] = useState(false);
  const selected = parseNoZ(value);

  return (
    <div className={cn('relative min-w-50', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'flex h-8 w-full items-center gap-2 rounded-md border bg-white px-3 text-sm outline-none transition-colors',
                open ? 'border-brand-primary' : 'border-border-default hover:border-text-muted',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            />
          }
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
          <span className={cn('truncate', selected ? 'text-text-primary' : 'text-text-muted')}>
            {selected ? formatServiceDate(value) : 'Service date'}
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2">
          <Calendar
            mode="single"
            selected={selected ?? undefined}
            onSelect={(d) => {
              if (d) {
                // Preserve the backend "service added" time; change only the date.
                onChange(`${toYmd(d)}T${timePart(value)}`);
                setOpen(false);
              }
            }}
            autoFocus
            // Flutter `lastDate: DateTime.now()` — no future service dates.
            disabled={{ after: new Date() }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
